/**
 * Hook `useQuestions`
 * 
 * Este hook proporciona funcionalidades para cargar preguntas, asociarlas a indicadores y actualizar el estado de preguntas asociadas.
 * Maneja el flujo de trabajo para la relación entre indicadores y preguntas en el sistema KPI.
 *
 * @returns {object} Un objeto que contiene el estado de las preguntas asociadas y las funciones para manejarlas.
 */

import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import { useAlert } from 'src/hooks/useAlert'
import useLoading from 'src/hooks/useLoading'
import {
  associateQuestionsToIndicator,
  getQuestiosForm,
} from 'src/services/KPI/Questions/Questions.service'
import { IFilterIndicators, IPayloadAsociateQuestions } from 'src/services/dashboard/indicator/indicator.interface'
import { setAssociatedQuestion } from 'src/redux/Dashboard/Questions/QuestionsSlice'
import { IQuestionState } from 'src/redux/Dashboard/Questions/Questions.interface'
import { useIndicators } from '../Indicators/useIndicators'

export const useQuestions = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { loadIndicator } = useIndicators()
  const { associatedQuestion } = useSelector(
    (state: RootState) => state.questions,
  )

  /**
   * Carga el listado de preguntas basado en los parámetros de paginación y otros filtros.
   * Muestra alertas según el resultado de la operación.
   *
   * @function loadQuestions
   * @param {object} pagination - Parámetros de paginación y filtros.
   * @param {number} [pagination.currentPage=null] - Página actual para la paginación.
   * @param {number} [pagination.pageSize=null] - Tamaño de la página para la paginación.
   * @param {string} [pagination.areaName=null] - Nombre del área para filtrar preguntas.
   * @param {number} [pagination.indicatorId=null] - ID del indicador para filtrar preguntas.
   * @param {number} [pagination.periodId=null] - ID del periodo para filtrar preguntas.
   * @param {number} [pagination.quarterId=null] - ID del trimestre para filtrar preguntas.
   * @param {number} [pagination.channelId=null] - ID del canal para filtrar preguntas.
   * @throws {Error} Si ocurre un error inesperado durante la carga de preguntas.
   */
  const loadQuestions = (pagination: {
    currentPage?: number | null
    pageSize?: number | null
    areaName?: string | null
    indicatorId?: number | null
    periodId?: number | null
    quarterId?: number | null
    channelId?: number | null
  }) => {
    try {
      startLoading()
      dispatch(getQuestiosForm({}))
        .then((result: any) => {
          if (getQuestiosForm.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las estrategias',
            )
          } else if (getQuestiosForm.fulfilled.match(result)) {
            showAlert('success', 'Estrategias cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Asocia preguntas a un indicador específico.
   * Actualiza el indicador con las preguntas asociadas y muestra alertas según el resultado de la operación.
   *
   * @function joinQuestionToIndicator
   * @param {IPayloadAsociateQuestions} indicator - Los datos del indicador y las preguntas a asociar.
   * @throws {Error} Si ocurre un error al asociar las preguntas al indicador.
   */
  const joinQuestionToIndicator = (indicator: IPayloadAsociateQuestions) => {
    try {
      startLoading()
      dispatch(associateQuestionsToIndicator(indicator))
        .then((result: any) => {
          if (associateQuestionsToIndicator.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al asociar preguntas',
            )
          } else if (associateQuestionsToIndicator.fulfilled.match(result)) {
            loadIndicator({
              currentPage: 1,
              pageSize: 25,
              indicatorId: String(indicator.indicatorId),
            })
            showAlert('success', 'Preguntas asociadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Actualiza el estado de las preguntas asociadas en el store de Redux.
   *
   * @function updateAssociatedQuestion
   * @param {IQuestionState['associatedQuestion']} question - Las preguntas asociadas que se actualizarán en el estado.
   */
  const updateAssociatedQuestion = (
    question: IQuestionState['associatedQuestion'],
  ) => {
    setAssociatedQuestion(question)
  }

  return {
    associatedQuestion,
    updateAssociatedQuestion,
    loadQuestions,
    joinQuestionToIndicator,
  }
}

export default useQuestions
